import { Event as RouterEvent, NavigationEnd, NavigationStart, Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { Observable, Subscription } from 'rxjs';
import { SanitizeUtil } from '../services/sanitize.util';

/** A Sentry integration, as the SDK's integration factories return it. */
export type SentryIntegration = ReturnType<typeof Sentry.browserTracingIntegration>;

/** How a Replay records: the whole session, or a rolling buffer sent only with an error. */
export type ReplayRecordingMode = 'session' | 'buffer';

/** What the gate uses of a running Replay. */
export interface ReplayHandle {
	getRecordingMode(): ReplayRecordingMode | undefined;
	stop(): Promise<void>;
	start(): void;
	startBuffering(): void;
}

/** The Sentry calls the gate makes; a spec swaps them, since a webpack namespace import cannot be spied on. */
export interface ReplayControls {
	/** False with Sentry disabled: there is no recording to gate. */
	hasClient(): boolean;
	getReplay(): ReplayHandle | undefined;
	addIntegration(integration: SentryIntegration): void;
}

const SENTRY_REPLAY_CONTROLS: ReplayControls = {
	hasClient: () => !!Sentry.getClient(),
	getReplay: () => Sentry.getReplay(),
	addIntegration: (integration) => Sentry.addIntegration(integration),
};

/**
 * Text masking stays as it was. Inputs stay masked (`maskAllInputs` defaults to true) and request
 * bodies stay out of recordings (no `networkDetailAllowUrls`): that is what keeps typed passwords
 * and card numbers out of Replay.
 */
const REPLAY_OPTIONS: Parameters<typeof Sentry.replayIntegration>[0] = {
	// NOTE: This will disable built-in masking. Only use this if your site has no sensitive data
	maskAllText: false,
	blockAllMedia: false,
	beforeAddRecordingEvent: SanitizeUtil.redactReplayFrame,
};

/**
 * Keeps Session Replay from recording while the page URL holds a password-reset or SSO token.
 *
 * Every recording starts with an rrweb Meta event carrying `location.href`, and buffer mode takes
 * a new one every minute. No SDK hook sees that event (`beforeAddRecordingEvent` gets only
 * Replay's own frames), so the only way to keep the token out of it is not to record then:
 * - a page loaded on a token URL (the emailed reset link, the IdP's redirect to /sso-relay)
 *   starts without Replay, which is added once the app has navigated to a URL without one;
 * - an in-app navigation to a token URL stops a running Replay, which restarts in the same mode
 *   when the app navigates away.
 * Replay's own frames and replay events are scrubbed as well (redactReplayFrame,
 * replayUrlScrubIntegration), since the navigation away still names the URL it left.
 */
export class ReplayTokenGate {
	private replay: SentryIntegration | undefined;
	/** How the Replay this gate stopped was recording; undefined while nothing is stopped. */
	private stoppedMode: ReplayRecordingMode | undefined;

	constructor(private readonly controls: ReplayControls,
				private readonly createReplay: () => SentryIntegration) {
	}

	/** Replay for Sentry.init, unless the page was loaded on a token URL. */
	integrationsAtLoad(href: string): SentryIntegration[] {
		return SanitizeUtil.urlCarriesToken(href) ? [] : [this.replayIntegration()];
	}

	/** Follows the router for the life of the app. Never throws into navigation. */
	watch(events: Observable<RouterEvent>, currentHref: () => string = () => window.location.href): Subscription {
		return events.subscribe((event) => {
			try {
				if (event instanceof NavigationStart && SanitizeUtil.urlCarriesToken(event.url)) {
					this.pause();
				} else if (event instanceof NavigationEnd
					&& !SanitizeUtil.urlCarriesToken(event.urlAfterRedirects)
					&& !SanitizeUtil.urlCarriesToken(currentHref())) {
					this.resume();
				}
			} catch {
				// A recording decision is never worth a broken navigation.
			}
		});
	}

	private pause(): void {
		const replay = this.controls.getReplay();
		const mode = replay?.getRecordingMode();
		if (replay && mode) {
			this.stoppedMode = mode;
			// Recording ends synchronously inside stop(); the promise is only the final flush of what
			// was recorded before this navigation, and stop() reports its own failures to Sentry.
			void replay.stop();
		}
	}

	private resume(): void {
		if (!this.controls.hasClient()) {
			return;
		}
		const replay = this.controls.getReplay();
		if (!replay) {
			this.controls.addIntegration(this.replayIntegration());
		} else if (this.stoppedMode === 'session') {
			replay.start();
		} else if (this.stoppedMode === 'buffer') {
			replay.startBuffering();
		}
		this.stoppedMode = undefined;
	}

	/** One instance only: Replay throws when a second one is constructed. */
	private replayIntegration(): SentryIntegration {
		if (!this.replay) {
			this.replay = this.createReplay();
		}
		return this.replay;
	}
}

export const replayTokenGate = new ReplayTokenGate(SENTRY_REPLAY_CONTROLS, () => Sentry.replayIntegration(REPLAY_OPTIONS));

export function initReplayTokenGate(router: Router): () => void {
	return () => {
		replayTokenGate.watch(router.events);
	};
}

/**
 * Scrubs replay events, which list every URL a recording visited and carry the page URL.
 * `beforeSend` never sees them; an integration's event processor does.
 */
export function replayUrlScrubIntegration(): SentryIntegration {
	return {
		name: 'PortalReplayUrlScrub',
		processEvent: (event) => event.type === 'replay_event' ? SanitizeUtil.redactReplayEvent(event) : event,
	};
}
